<template>
	<div class="login-page">
		<div class="login-page__content">
			<header class="login-page__header">
				<h1 v-if="panel === 'main'" class="login-page__welcome">{{ t('WelcomeTo') }}</h1>
				<img src="/m.eagle/img/portal/logo-white.png" alt="eCOS" class="login-page__logo" />
			</header>

			<v-form v-if="panel === 'main'" class="login-page__form" @submit.prevent="submitLogin">
				<v-text-field :model-value="form.userName" :label="t('UserName')" prepend-inner-icon="person" autocomplete="username" v-bind="fieldProps" @update:model-value="(value) => edit('userName', loginCode(value))" />
				<v-text-field :model-value="form.password" :label="t('Password')" :type="showPassword ? 'text' : 'password'" prepend-inner-icon="password" :append-inner-icon="showPassword ? 'visibility_off' : 'visibility'" autocomplete="current-password" v-bind="fieldProps" @click:append-inner="showPassword = !showPassword" @update:model-value="(value) => edit('password', value)" />
				<v-text-field :model-value="form.customerCode" :label="t('CustomerCode')" prepend-inner-icon="group" autocomplete="organization" v-bind="fieldProps" @update:model-value="(value) => edit('customerCode', loginCode(value))" />

				<div class="login-page__options">
					<v-checkbox :model-value="form.remember" :label="t('RememberMe')" color="white" density="compact" hide-details class="login-page__remember" @update:model-value="(value) => edit('remember', value)" />
					<button type="button" class="login-page__link" @click="showForgotten">{{ t('ForgottenPassword') }}</button>
				</div>

				<v-btn type="submit" block size="large" rounded="xl" variant="flat" color="primary" :loading="legacyLogin.busy" :disabled="!legacyLogin.ready || !canSubmitLogin(form)">{{ t('Login') }}</v-btn>

				<template v-if="legacyLogin.ssoCustomer">
					<div class="login-page__divider">
						<span>{{ t('SSOor') }}</span>
					</div>
					<v-btn block size="large" rounded="xl" variant="outlined" color="secondary" :disabled="legacyLogin.busy" @click="ssoLegacyLogin">{{ t('SSO {0}', [legacyLogin.ssoCustomer]) }}</v-btn>
				</template>
			</v-form>

			<v-form v-else-if="panel === 'forgotten'" class="login-page__form" @submit.prevent="requestReset">
				<p class="login-page__text">
					<strong>{{ t('PasswordRecovery') }}</strong>
					<span>{{ t('InstructionsByMail') }}</span>
				</p>
				<v-text-field v-model="reset.email" :label="t('Email')" type="email" prepend-inner-icon="email" autocomplete="email" v-bind="fieldProps" />
				<v-text-field :model-value="reset.customer" :label="t('CustomerCode')" prepend-inner-icon="group" autocomplete="organization" v-bind="fieldProps" @update:model-value="(value) => (reset.customer = loginCode(value))" />
				<v-btn type="submit" block size="large" rounded="xl" variant="flat" color="primary" :loading="legacyLogin.busy" :disabled="!canRequestReset(reset)">{{ t('ResetPassword') }}</v-btn>
				<v-btn block size="large" rounded="xl" variant="text" color="white" @click="backToLegacyLogin">{{ t('Back') }}</v-btn>
			</v-form>

			<v-form v-else-if="panel === 'reset'" class="login-page__form" @submit.prevent="changePassword">
				<v-text-field v-model="change.email" :label="t('Email')" type="email" prepend-inner-icon="email" autocomplete="email" v-bind="fieldProps" />
				<v-text-field :model-value="change.customer" :label="t('CustomerCode')" prepend-inner-icon="group" autocomplete="organization" v-bind="fieldProps" @update:model-value="(value) => (change.customer = loginCode(value))" />
				<v-text-field v-model="change.key" :label="t('Key')" prepend-inner-icon="key" autocomplete="one-time-code" v-bind="fieldProps" />
				<v-text-field v-model="change.password" :label="t('Password')" :type="showPassword ? 'text' : 'password'" prepend-inner-icon="password" :append-inner-icon="showPassword ? 'visibility_off' : 'visibility'" autocomplete="new-password" v-bind="fieldProps" @click:append-inner="showPassword = !showPassword" />
				<v-text-field v-model="change.confirm" :label="t('ConfirmPassword')" :type="showPassword ? 'text' : 'password'" prepend-inner-icon="password" autocomplete="new-password" v-bind="fieldProps" />
				<v-btn type="submit" block size="large" rounded="xl" variant="flat" color="primary" :loading="legacyLogin.busy" :disabled="!canChangePassword(change)">{{ t('ChangePassword') }}</v-btn>
			</v-form>

			<v-form v-else class="login-page__form" @submit.prevent="updateExpiredPassword">
				<p class="login-page__text login-page__text--multiline">{{ expiredText }}</p>
				<v-text-field v-model="expired.oldPassword" :label="t('OldPassword')" :type="showPassword ? 'text' : 'password'" prepend-inner-icon="password" :append-inner-icon="showPassword ? 'visibility_off' : 'visibility'" autocomplete="current-password" v-bind="fieldProps" @click:append-inner="showPassword = !showPassword" />
				<v-text-field v-model="expired.newPassword" :label="t('NewPassword')" :type="showPassword ? 'text' : 'password'" prepend-inner-icon="password" autocomplete="new-password" v-bind="fieldProps" />
				<v-btn type="submit" block size="large" rounded="xl" variant="flat" color="primary" :loading="legacyLogin.busy" :disabled="!canUpdateExpiredPassword(expired)">{{ t('ChangePassword') }}</v-btn>
			</v-form>
		</div>

		<footer class="login-page__footer">
			<a href="https://www.externis.com" target="_blank" rel="noopener" class="login-page__credits">
				<span>Powered by</span>
				<ExternisLogo />
			</a>

			<v-menu location="top end" offset="8">
				<template #activator="{ props }">
					<v-btn v-bind="props" variant="text" color="white" prepend-icon="language" append-icon="expand_less" :aria-label="t('ChooseYourLanguage')">{{ currentLanguage.toUpperCase() }}</v-btn>
				</template>
				<v-list density="compact" rounded="lg" class="login-page__languages">
					<v-list-subheader>{{ t('ChooseYourLanguage') }}</v-list-subheader>
					<v-list-item v-for="language in LOGIN_LANGUAGES" :key="language.locale" :title="language.label" :active="language.locale.startsWith(currentLanguage)" @click="changeLoginLanguage(language.locale)">
						<template #prepend>
							<img :src="languageFlag(language.locale)" alt="" class="login-page__flag" />
						</template>
					</v-list-item>
				</v-list>
			</v-menu>
		</footer>
	</div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import ExternisLogo from '@/components/app/ExternisLogo.vue';
import { useTranslator } from '@/composables/useTranslator';
import { getActiveLocale } from '@/i18n';
import { toPlainText } from '@/iframe/legacyDialogs';
import { backToLegacyLogin, changeLegacyPassword, changeLoginLanguage, legacyLogin, requestLegacyPasswordReset, showLegacyForgottenPassword, ssoLegacyLogin, submitLegacyLogin, updateLegacyExpiredPassword } from '@/iframe/legacyLogin';
import { LOGIN_LANGUAGES, canChangePassword, canRequestReset, canSubmitLogin, canUpdateExpiredPassword, languageFlag, loginCode } from '@/utils/domain/loginModel';

const t = useTranslator();

const fieldProps = { variant: 'solo', flat: true, rounded: 'lg', bgColor: 'white', density: 'comfortable', hideDetails: true };

const form = reactive({ userName: '', password: '', customerCode: '', remember: false });
const reset = reactive({ email: '', customer: '' });
const change = reactive({ email: '', customer: '', key: '', password: '', confirm: '' });
const expired = reactive({ oldPassword: '', newPassword: '' });
const showPassword = ref(false);
const edited = ref(false);

const panel = computed(() => legacyLogin.panel);
const currentLanguage = computed(() => String(getActiveLocale() || 'fr-FR').split('-')[0]);
const expiredText = computed(() => toPlainText(t('PleaseSetNewPass'), true));

watch(
	() => legacyLogin.prefill,
	(prefill) => {
		if (edited.value) return;
		form.userName = prefill.userName;
		form.customerCode = prefill.customerCode;
		form.remember = prefill.remember;
	},
	{ immediate: true }
);

watch(panel, () => (showPassword.value = false));

const edit = (key, value) => {
	edited.value = true;
	form[key] = value;
};

const submitLogin = () => {
	if (canSubmitLogin(form)) submitLegacyLogin(form);
};

const showForgotten = () => {
	reset.customer = form.customerCode;
	showLegacyForgottenPassword({ customerCode: form.customerCode });
};

const requestReset = () => {
	if (canRequestReset(reset)) requestLegacyPasswordReset(reset);
};

const changePassword = () => {
	if (canChangePassword(change)) changeLegacyPassword(change);
};

const updateExpiredPassword = () => {
	if (canUpdateExpiredPassword(expired)) updateLegacyExpiredPassword(expired);
};
</script>
